// Assets and maintenance (docs/plans/phase3-operations-modules.md §3): tags, status, the
// planned-service due date and window, and the work-order steps. Pure: callers pass the time.

import { err, ok, type Result } from '../result.ts';
import { addDays, compareDays, eatDayOf, formatIsoDay, parseIsoDay } from '../time.ts';

export const ASSET_STATUSES = ['in_service', 'out_of_service', 'condemned'] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];

export const ASSET_STATUS_LABELS: Record<AssetStatus, string> = {
  in_service: 'In service',
  out_of_service: 'Out of service',
  condemned: 'Condemned',
};

export const WORK_ORDER_KINDS = ['planned', 'breakdown'] as const;
export type WorkOrderKind = (typeof WORK_ORDER_KINDS)[number];

export const WORK_ORDER_KIND_LABELS: Record<WorkOrderKind, string> = {
  planned: 'Planned maintenance',
  breakdown: 'Breakdown',
};

export const WORK_ORDER_STATUSES = ['open', 'in_progress', 'completed', 'cancelled'] as const;
export type WorkOrderStatus = (typeof WORK_ORDER_STATUSES)[number];

export const WORK_ORDER_STATUS_LABELS: Record<WorkOrderStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/** Maintenance due within this many days shows on the due list (plan A1). */
export const MAINTENANCE_DUE_WINDOW_DAYS = 14;

export const ASSET_TEXT_LIMITS = {
  name: 120,
  location: 120,
  department: 80,
  makeModel: 120,
  serial: 60,
  problem: 2000,
  workDone: 2000,
} as const;

export type AssetErrorCode =
  | 'asset.tag_invalid'
  | 'asset.status_unchanged'
  | 'asset.condemned_is_final'
  | 'work_order.not_allowed';

export const ASSET_ERROR_MESSAGES: Record<AssetErrorCode, string> = {
  'asset.tag_invalid':
    'Tags are letters, digits and hyphens, up to 30 characters, like DMC-INF-003.',
  'asset.status_unchanged': 'The asset already has that status.',
  'asset.condemned_is_final': 'A condemned asset stays condemned. Register a replacement instead.',
  'work_order.not_allowed':
    "That step doesn't fit this work order now. Reload it to see its status, then try again.",
};

const TAG_PATTERN = /^[A-Z0-9-]{1,30}$/;

/** Tags are unique in the organisation and stored upper case (plan A2). */
export function checkAssetTag(tag: string): Result<string, AssetErrorCode> {
  const value = tag.trim().toUpperCase();
  return TAG_PATTERN.test(value) ? ok(value) : err('asset.tag_invalid');
}

/** In service ⇄ out of service; either can be condemned, which is final. Never deleted. */
export function assetStatusChange(
  from: AssetStatus,
  to: AssetStatus,
): Result<AssetStatus, AssetErrorCode> {
  if (from === 'condemned') return err('asset.condemned_is_final');
  if (from === to) return err('asset.status_unchanged');
  return ok(to);
}

/**
 * The next planned service: the last completed planned work order's day (or, before the
 * first, the start day: purchase or registration) plus the interval. Null without an interval.
 */
export function nextServiceDue(input: {
  intervalDays: number | null;
  lastServicedOn: string | null;
  startOn: string;
}): string | null {
  if (input.intervalDays === null) return null;
  const base = parseIsoDay(input.lastServicedOn ?? input.startOn);
  if (!base.ok) return null;
  return formatIsoDay(addDays(base.value, input.intervalDays));
}

/** Overdue from the day after the due day (EAT); due soon on it and within the window. */
export function maintenanceDueState(
  dueOn: string | null,
  now: Date,
  windowDays: number = MAINTENANCE_DUE_WINDOW_DAYS,
): 'overdue' | 'due_soon' | null {
  if (dueOn === null) return null;
  const due = parseIsoDay(dueOn);
  if (!due.ok) return null;
  const today = eatDayOf(now);
  if (compareDays(due.value, today) < 0) return 'overdue';
  if (compareDays(due.value, addDays(today, windowDays)) <= 0) return 'due_soon';
  return null;
}

/** open → in progress → completed; open or in progress → cancelled; nothing after. */
export function workOrderTransition(
  from: WorkOrderStatus,
  to: WorkOrderStatus,
): Result<WorkOrderStatus, AssetErrorCode> {
  const allowed: Record<WorkOrderStatus, readonly WorkOrderStatus[]> = {
    open: ['in_progress', 'completed', 'cancelled'],
    in_progress: ['completed', 'cancelled'],
    completed: [],
    cancelled: [],
  };
  return allowed[from].includes(to) ? ok(to) : err('work_order.not_allowed');
}
